/**
 * Team Spain v0 — Definición de Permisos y Matriz de Control de Acceso (Hito M1)
 * 
 * Reglas de aislamiento y seguridad:
 * 1. El rol 'admin' técnico NO concede acceso a datos clínicos privados.
 * 2. Los datos clínicos privados ('health_incidents_private') son accesibles ÚNICAMENTE
 *    por usuarios con rol 'medic' en el equipo, o por el propio atleta respecto a sí mismo.
 * 3. Los entrenadores ('coach') y preparadores ('trainer') acceden exclusivamente a la
 *    disponibilidad operativa ('availability_assessments'), nunca a diagnósticos ni notas confidenciales.
 * 4. Aislamiento multi-tenant estricto: un usuario no puede acceder a recursos de otra organización.
 * 5. Los roles y membresías solo pueden ser asignados por administradores; los usuarios no pueden autoasignárselos.
 */

export type AppRole = 'admin' | 'coach' | 'assistant_coach' | 'trainer' | 'medic' | 'athlete'

export const ROLE_CAPABILITIES: Record<string, readonly string[]> = {
  admin: ['manage_users'],
  coach: ['manage_team_sports', 'manage_roster', 'invite_athletes', 'manage_events', 'manage_playbook', 'capture_field', 'view_stats', 'view_workload', 'view_form_compliance'],
  assistant_coach: ['capture_field', 'view_stats', 'view_workload', 'view_form_compliance'],
  trainer: ['view_workload', 'view_form_compliance'],
  medic: ['manage_health', 'publish_availability', 'view_workload', 'view_form_compliance'],
  athlete: [],
}
export function hasOperation(roles: readonly string[], capabilities: readonly string[], operation: string): boolean {
  return capabilities.includes(operation) || roles.some((role) => ROLE_CAPABILITIES[role]?.includes(operation))
}

export interface UserMembershipInfo {
  organizationId: string
  teamId?: string | null
  roles: AppRole[]
  capabilities?: string[]
  isActive: boolean
}

/**
 * Comprueba si un usuario puede leer notas clínicas privadas.
 * REGLA ESTRICTA: Solo rol 'medic' o el propio atleta sobre sus datos. Admin técnico = DENEGADO.
 */
export function canViewHealthPrivate(roles: AppRole[], isSelf: boolean): boolean {
  if (isSelf) return true
  return roles.includes('medic')
}

/**
 * Comprueba si un usuario puede escribir o editar notas clínicas privadas.
 * REGLA ESTRICTA: Únicamente rol 'medic'. Admin técnico = DENEGADO.
 */
export function canManageHealthPrivate(roles: AppRole[]): boolean {
  return roles.includes('medic')
}

/**
 * Comprueba si un usuario puede ver el resumen de disponibilidad operativa deportiva.
 * Visible para cuerpo técnico, médico y deportistas del equipo.
 */
export function canViewOperationalAvailability(roles: AppRole[], isMemberOfTeam: boolean): boolean {
  if (!isMemberOfTeam) return false
  return roles.some((r) => ['coach', 'assistant_coach', 'trainer', 'medic', 'athlete', 'admin'].includes(r))
}

/**
 * Comprueba si un usuario puede registrar o actualizar valoraciones de disponibilidad operativa.
 * Publicación reservada al rol sanitario ('medic').
 */
export function canManageOperationalAvailability(roles: AppRole[]): boolean {
  return roles.includes('medic')
}

/**
 * Comprueba si un usuario puede gestionar miembros y roles de la organización.
 * Solo administradores.
 */
export function canManageMemberships(roles: AppRole[], isSystemAdmin: boolean = false): boolean {
  return isSystemAdmin || roles.includes('admin')
}

/**
 * Comprueba si un usuario puede registrar capturas y jugadas en campo.
 * Requiere capture_field explícita o el valor predeterminado de coach/asistente.
 */
export function canManageSportCapture(roles: string[], capabilities: string[] = []): boolean {
  return hasOperation(roles, capabilities, 'capture_field')
}

/**
 * Comprueba si un usuario puede consultar estadísticas deportivas colectivas.
 * Permitido si tiene el rol 'coach', 'assistant_coach', o la capacidad efectiva 'view_stats'.
 */
export function canViewSportStats(roles: string[], capabilities: string[] = []): boolean {
  return hasOperation(roles, capabilities, 'view_stats')
}

/**
 * Aislamiento multi-tenant: valida pertenencia a la misma organización.
 */
export function canAccessOrganizationData(userOrgId: string, resourceOrgId: string): boolean {
  if (!userOrgId || !resourceOrgId) return false
  return userOrgId === resourceOrgId
}

/**
 * Aislamiento de equipo: valida pertenencia al equipo o alcance de organización.
 */
export function canAccessTeamData(
  userMembership: UserMembershipInfo,
  targetTeamId: string,
  targetOrgId: string
): boolean {
  if (!userMembership.isActive) return false
  if (userMembership.organizationId !== targetOrgId) return false
  // Si la membresía es a nivel de toda la organización (teamId nulo), tiene acceso
  if (!userMembership.teamId) return true
  return userMembership.teamId === targetTeamId
}
